import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface Organization {
  id: string;
  name: string;
  segment_type: 'oem' | 'lender' | 'insurer' | 'dealer' | 'marketplace' | 'service';
  subscription_tier: 'free' | 'basic' | 'pro' | 'enterprise';
  subscription_status: 'active' | 'suspended' | 'cancelled' | 'trial';
  features_enabled: string[];
  api_enabled: boolean;
  lead_quota_monthly: number;
  leads_used_this_month: number;
  logo_url?: string;
}

interface UseOrganizationReturn {
  organization: Organization | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  isAdmin: boolean;
  userRole: string | null;
}

export const useOrganization = (): UseOrganizationReturn => {
  const { user, session, isAuthenticated, isLoading: authLoading } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [roleCheckComplete, setRoleCheckComplete] = useState(false);

  const fetchOrganization = useCallback(async () => {
    if (!user?.id) {
      setRoleCheckComplete(true);
      return;
    }

    try {
      setError(null);

      // Use RPC function for reliable admin check (bypasses RLS issues)
      console.log('[useOrganization] Checking admin role for user:', user.id);
      const { data: isAdminData, error: isAdminError } = await supabase
        .rpc('has_role', { _user_id: user.id, _role: 'internal_admin' });

      if (isAdminError) {
        console.error('[useOrganization] RPC has_role error:', isAdminError);
      }

      const adminStatus = isAdminData === true;
      console.log('[useOrganization] Admin check result:', { isAdminData, adminStatus });
      setIsAdmin(adminStatus);
      
      if (adminStatus) {
        setUserRole('internal_admin');
      }

      // For non-admins or to get org details, still fetch role data
      if (!adminStatus) {
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role, organization_id, organization_name')
          .eq('user_id', user.id)
          .maybeSingle();

        if (roleError && roleError.code !== 'PGRST116') {
          console.error('[useOrganization] Role query error:', roleError);
        }

        if (roleData) {
          setUserRole(roleData.role);

          // If user has an organization, fetch its details
          if (roleData.organization_id) {
            const { data: orgData, error: orgError } = await supabase
              .from('partner_organizations_safe')
              .select('id, name, segment_type, subscription_tier, subscription_status, features_enabled, api_enabled, lead_quota_monthly, leads_used_this_month, logo_url')
              .eq('id', roleData.organization_id)
              .single();

            if (orgError && orgError.code !== 'PGRST116') {
              console.error('[useOrganization] Org query error:', orgError);
            }

            if (orgData) {
              setOrganization({
                id: orgData.id,
                name: orgData.name,
                segment_type: orgData.segment_type as Organization['segment_type'],
                subscription_tier: orgData.subscription_tier as Organization['subscription_tier'],
                subscription_status: orgData.subscription_status as Organization['subscription_status'],
                features_enabled: (orgData.features_enabled as string[]) || [],
                api_enabled: orgData.api_enabled || false,
                lead_quota_monthly: orgData.lead_quota_monthly || 10,
                leads_used_this_month: orgData.leads_used_this_month || 0,
                logo_url: orgData.logo_url || undefined,
              });
            }
          }
        }
      }
    } catch (err) {
      console.error('[useOrganization] Error fetching organization:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch organization');
    } finally {
      setRoleCheckComplete(true);
    }
  }, [user?.id]);

  // Reset state when user logs out
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      setOrganization(null);
      setIsAdmin(false);
      setUserRole(null);
      setRoleCheckComplete(true);
    }
  }, [authLoading, isAuthenticated]);

  // Fetch when we have both authentication AND a valid session
  useEffect(() => {
    if (!authLoading && isAuthenticated && session && user?.id) {
      setRoleCheckComplete(false);
      fetchOrganization();
    }
  }, [authLoading, isAuthenticated, session, user?.id, fetchOrganization]);

  // Compute loading: true until auth is done AND role check is complete
  const computedLoading = authLoading || (!roleCheckComplete && isAuthenticated);

  return {
    organization,
    loading: computedLoading,
    error,
    refetch: fetchOrganization,
    isAdmin,
    userRole,
  };
};
